import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RiRefreshLine } from '@remixicon/react';
import { cn } from '@/lib/utils';
import { easeOut } from '@/components/landing/marketing-motion';
import logo from '@/assets/site_logo_dark.png';

interface FullPageLoaderProps {
  text?: string;
  className?: string;
  /** overlay = covers the viewport; page = standalone screen; contained = fills parent */
  variant?: 'overlay' | 'page' | 'contained';
}

/** Skip the reveal delay for fast loads so the loader doesn't flash on screen. */
const REVEAL_DELAY_MS = 180;
/** Loaders mounted back-to-back (auth → stores → dashboard) read as one continuous screen. */
const CONTINUITY_MS = 700;
const SLOW_AFTER_MS = 8000;
const STALLED_AFTER_MS = 20000;

let lastVisibleAt = 0;

function useLoaderTimeline() {
  const [continuing] = useState(() => Date.now() - lastVisibleAt < CONTINUITY_MS);
  const [visible, setVisible] = useState(continuing);
  const [stage, setStage] = useState<'normal' | 'slow' | 'stalled'>('normal');

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setVisible(true), continuing ? 0 : REVEAL_DELAY_MS),
      window.setTimeout(() => setStage('slow'), SLOW_AFTER_MS),
      window.setTimeout(() => setStage('stalled'), STALLED_AFTER_MS),
    ];
    const heartbeat = window.setInterval(() => {
      lastVisibleAt = Date.now();
    }, 200);
    return () => {
      timers.forEach(window.clearTimeout);
      window.clearInterval(heartbeat);
      lastVisibleAt = Date.now();
    };
  }, [continuing]);

  return { visible, continuing, stage };
}

function LoaderStatus({ text, stage }: { text?: string; stage: 'normal' | 'slow' | 'stalled' }) {
  const message =
    stage === 'normal' ? text : stage === 'slow' ? 'Still working, almost there' : 'This is taking longer than usual';

  return (
    <div className="flex min-h-[3.25rem] flex-col items-center gap-3">
      <AnimatePresence mode="wait" initial={false}>
        {message ? (
          <motion.p
            key={message}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: easeOut }}
            className="text-center text-sm font-medium tracking-tight text-muted-foreground"
          >
            {message}
          </motion.p>
        ) : null}
      </AnimatePresence>
      {stage === 'stalled' ? (
        <motion.button
          type="button"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => window.location.reload()}
          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card px-4 text-[13px] font-semibold text-foreground transition-colors hover:bg-accent"
        >
          <RiRefreshLine className="h-4 w-4" />
          Reload page
        </motion.button>
      ) : null}
    </div>
  );
}

function IndeterminateBar({ className }: { className?: string }) {
  return (
    <div className={cn('h-1 overflow-hidden rounded-full bg-primary/10', className)} aria-hidden>
      <div className="h-full w-2/5 rounded-full bg-gradient-to-r from-[#be9afc] via-primary to-[#be9afc] animate-loader-bar" />
    </div>
  );
}

export function FullPageLoader({
  text = 'Loading',
  className,
  variant = 'overlay',
}: FullPageLoaderProps) {
  const { visible, continuing, stage } = useLoaderTimeline();
  const contained = variant === 'contained';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={text}
      className={cn(
        'relative flex flex-col items-center justify-center overflow-hidden',
        variant === 'overlay' && 'fixed inset-0 z-50 bg-background',
        variant === 'page' && 'min-h-dvh bg-background',
        contained && 'min-h-[52vh] w-full py-16',
        className,
      )}
    >
      {visible ? (
        <motion.div
          initial={continuing ? false : { opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.32, ease: easeOut }}
          className="relative flex flex-col items-center"
        >
          {contained ? (
            <div className="flex flex-col items-center gap-5 px-6">
              <IndeterminateBar className="w-28" />
              <LoaderStatus text={text} stage={stage} />
            </div>
          ) : (
            <>
              <div
                className="pointer-events-none fixed -right-24 -top-24 size-80 rounded-full bg-[#d4c4fc]/40 blur-3xl"
                aria-hidden
              />
              <div
                className="pointer-events-none fixed -bottom-28 -left-20 size-96 rounded-full bg-[#d2e4c4]/45 blur-3xl"
                aria-hidden
              />

              <div className="relative flex flex-col items-center gap-7 px-6">
                <div className="relative">
                  <div
                    className="absolute -inset-5 rounded-[2rem] bg-primary/15 blur-2xl animate-loader-breathe"
                    aria-hidden
                  />
                  <div className="relative overflow-hidden">
                    <img src={logo} alt="Sqaleshop" className="brand-mark relative h-7 sm:h-8" />
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent animate-skeleton-sweep [animation-duration:2.6s]"
                      aria-hidden
                    />
                  </div>
                </div>

                <IndeterminateBar className="w-40" />
                <LoaderStatus text={text} stage={stage} />
              </div>
            </>
          )}
        </motion.div>
      ) : null}
    </div>
  );
}

/** Compact loader for content already inside a layout. */
export function PageLoader({
  text = 'Loading',
  className,
}: Omit<FullPageLoaderProps, 'variant'>) {
  return <FullPageLoader text={text} variant="contained" className={className} />;
}
